<template>
  <div class="max-w-7xl mx-auto px-4 py-8 font-inter">
    <!-- Page heading -->
    <div class="flex flex-col gap-2 mb-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold text-gray-900">Talent Pools</h1>
          <p class="text-gray-600 text-sm">Search and manage your pools; sort by applicants or recency.</p>
        </div>
        <button
          class="inline-flex items-center gap-2 rounded-lg bg-newPrimary text-white px-4 py-2.5 text-sm font-semibold shadow-sm hover:bg-newPrimary/90 transition"
          @click="createModal?.show()"
        >
          <span class="text-lg leading-none">+</span> Create Talent Pool
        </button>
      </div>
    </div>

    <!-- Controls -->
    <div class="flex flex-col lg:flex-row lg:items-center gap-3 mb-6">
      <div class="flex-1">
        <div class="relative">
          <Search class="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search by title, location, work type, or requirement"
            class="w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 py-2.5 text-sm text-gray-800 focus:border-newPrimary focus:ring-2 focus:ring-newPrimary/20 transition"
          />
        </div>
      </div>
      <div class="flex items-center gap-3">
        <label class="text-sm text-gray-600">Sort by</label>
        <select
          v-model="sortBy"
          class="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:border-newPrimary focus:ring-2 focus:ring-newPrimary/20"
        >
          <option value="newest">Newest</option>
          <option value="applicants">Most applicants</option>
          <option value="title">Title A → Z</option>
          <option value="timeline">Timeline earliest</option>
        </select>
      </div>
    </div>

    <!-- Stats -->
    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-6">
      <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
        <p class="text-[11px] uppercase tracking-[0.12em] text-gray-500 font-semibold">Total pools</p>
        <p class="text-2xl font-semibold text-slate-900 leading-none mt-1">{{ stats.totalPools }}</p>
        <p class="text-[11px] text-gray-500 mt-1">Active pipelines</p>
      </div>
      <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
        <p class="text-[11px] uppercase tracking-[0.12em] text-gray-500 font-semibold">Total members</p>
        <p class="text-2xl font-semibold text-slate-900 leading-none mt-1">{{ stats.totalApplicants }}</p>
        <p class="text-[11px] text-gray-500 mt-1">Across all pools</p>
      </div>
      <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
        <p class="text-[11px] uppercase tracking-[0.12em] text-gray-500 font-semibold">Avg per pool</p>
        <p class="text-2xl font-semibold text-slate-900 leading-none mt-1">{{ stats.avgApplicants }}</p>
        <p class="text-[11px] text-gray-500 mt-1">Applicants per pool</p>
      </div>
    </div>

    <div v-if="isInitialLoading" class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      <div v-for="i in 6" :key="i" class="rounded-xl border border-gray-100 bg-white shadow-sm p-5 space-y-4 animate-pulse">
        <div class="h-4 w-32 bg-gray-100 rounded"></div>
        <div class="h-3 w-48 bg-gray-100 rounded"></div>
        <div class="grid grid-cols-2 gap-3">
          <div class="h-12 bg-gray-100 rounded"></div>
          <div class="h-12 bg-gray-100 rounded"></div>
        </div>
        <div class="h-6 bg-gray-100 rounded"></div>
      </div>
    </div>

    <div v-else-if="filteredTalentPools.length > 0" class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      <router-link
        v-for="pool in filteredTalentPools"
        :key="pool.id"
        :to="{ name: 'CompanyTalentPoolDetail', params: { id: pool.id } }"
        class="block"
      >
        <div class="bg-white border border-gray-100 rounded-xl shadow-sm p-5 hover:-translate-y-1 hover:shadow-lg transition space-y-4">
          <div class="flex items-start justify-between gap-3">
            <div class="space-y-2">
              <h3 class="text-lg font-semibold text-gray-900 leading-tight">{{ pool.title }}</h3>
              <div class="flex flex-wrap items-center gap-2 text-sm text-gray-500">
                <span class="flex items-center gap-1"><Briefcase class="h-4 w-4 text-newPrimary" /> {{ pool.work_type || 'General' }}</span>
              </div>
              <div class="flex items-center gap-1 text-sm text-gray-500 max-w-[220px] truncate">
                <MapPin class="h-4 w-4 text-newPrimary" />
                <span class="truncate">{{ pool.location || 'Remote / Various' }}</span>
              </div>
              <div class="flex items-center gap-1 text-sm text-gray-500 max-w-[240px] truncate">
                <Calendar class="h-4 w-4 text-newPrimary" />
                <span class="truncate">
                  {{ formatHiringTimeline(pool.hiring_timeline, pool.timeline_date, pool.timeline) }}
                </span>
              </div>
            </div>
            <div class="text-right space-y-1">
              <span class="text-[11px] uppercase tracking-[0.08em] text-gray-500 font-semibold block">Created</span>
              <span class="text-xs font-semibold text-gray-900">{{ formatDate(pool.creation_date) }}</span>
              <span class="text-xs text-gray-500 block">ID #{{ pool.id }}</span>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div class="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
              <p class="text-[11px] uppercase tracking-[0.08em] text-gray-500 font-semibold">Applicants</p>
              <p class="text-lg font-semibold text-gray-900 leading-tight">{{ pool.applicants_count ?? 0 }}</p>
            </div>
            <div class="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
              <p class="text-[11px] uppercase tracking-[0.08em] text-gray-500 font-semibold">Type</p>
              <p class="text-sm font-semibold text-gray-800 leading-tight capitalize">{{ pool.type }}</p>
            </div>
          </div>

          <div class="flex flex-wrap gap-2">
            <span
              v-for="(req, idx) in (pool.requirements || []).slice(0, 2)"
              :key="'req-'+idx"
              class="text-xs px-2 py-1 rounded-full border border-gray-200 text-gray-700 bg-gray-50"
            >
              {{ req }}
            </span>
            <span
              v-for="(qual, idx) in (pool.qualifications || []).slice(0, 2)"
              :key="'qual-'+idx"
              class="text-xs px-2 py-1 rounded-full border border-gray-200 text-gray-700 bg-gray-50"
            >
              {{ qual }}
            </span>
            <span
              v-if="(pool.requirements?.length || 0) + (pool.qualifications?.length || 0) > 4"
              class="text-xs px-2 py-1 rounded-full border border-gray-200 text-gray-500 bg-gray-100"
            >
              +{{ (pool.requirements?.length || 0) + (pool.qualifications?.length || 0) - 4 }} more
            </span>
          </div>
        </div>
      </router-link>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-16">
      <div class="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
        <svg class="w-12 h-12 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
        </svg>
      </div>
      <h3 class="text-xl font-medium text-gray-900 mb-2 font-inter">No talent pools yet</h3>
      <p class="text-gray-500 max-w-md mx-auto mb-6">Create your first talent pool to start building a pipeline of qualified candidates for your open roles.</p>
    </div>
  </div>
  <CreateModal ref="createModal" />
</template>

<script setup lang="js">
// ===================================
// 1. IMPORTS
// ===================================
import { ref, onMounted, computed } from 'vue'
import { useTalentPoolsStore } from '@/stores/talentPoolsStore';
import { Briefcase, MapPin, Search, Calendar } from 'lucide-vue-next';
import { formatDate, formatHiringTimeline, getHiringTimelineSortKey } from '@/utils/formatters';
import { CreateModal } from '@/components/marketplace/talentPools'

// ===================================
// 2. PROPS & EMITS
// ===================================

// ===================================
// 3. STATE
// ===================================
const talentPoolsStore = useTalentPoolsStore();

const searchQuery = ref('');
const sortBy = ref('newest');
const createModal = ref(null);
const isInitialLoading = computed(() => talentPoolsStore.isLoading && !talentPoolsStore.talentPools.length);

// ===================================
// 4. COMPUTED PROPERTIES
// ===================================
const stats = computed(() => {
  const pools = talentPoolsStore.talentPools || [];
  const totalPools = pools.length;
  const totalApplicants = pools.reduce((sum, p) => sum + (Number(p.applicants_count) || 0), 0);
  const avgApplicants = totalPools ? Math.round(totalApplicants / totalPools) : 0;
  return {
    totalPools,
    totalApplicants,
    avgApplicants
  };
});

const filteredTalentPools = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  let pools = talentPoolsStore.talentPools || [];

  if (query) {
    pools = pools.filter(pool => {
      const title = (pool.title || '').toLowerCase();
      const location = (pool.location || '').toLowerCase();
      const workType = (pool.work_type || '').toLowerCase();
      const quals = (pool.qualifications || []).join(' ').toLowerCase();
      return title.includes(query) || location.includes(query) || workType.includes(query) || quals.includes(query);
    });
  }

  pools = [...pools].sort((a, b) => {
    switch (sortBy.value) {
      case 'applicants':
        return (b.applicants_count || 0) - (a.applicants_count || 0);
      case 'title':
        return (a.title || '').localeCompare(b.title || '');
      case 'timeline': {
        const aTime = getHiringTimelineSortKey(a.hiring_timeline, a.timeline_date, a.timeline, a.creation_date);
        const bTime = getHiringTimelineSortKey(b.hiring_timeline, b.timeline_date, b.timeline, b.creation_date);
        return aTime - bTime;
      }
      case 'newest':
      default:
        return new Date(b.creation_date || 0).getTime() - new Date(a.creation_date || 0).getTime();
    }
  });

  return pools;
});

// ===================================
// 5. WATCHERS
// ===================================

// ===================================
// 6. LIFECYCLE HOOKS
// ===================================
onMounted(() => {
  if (talentPoolsStore.talentPools.length) {
    talentPoolsStore.fetchTalentPools('company', { silent: true });
  } else {
    talentPoolsStore.fetchTalentPools('company');
  }
})
// ===================================
// 7. FUNCTIONS
// ===================================
// functions intentionally omitted; search/sort handled inline
</script>
